import { SettlementBooking } from "../types/settlementBooking";
import { hotelService } from "@/services/hotel-service";
import { useQuery } from "@tanstack/react-query";

export type SettlementType = "Arrivals" | "Departures" | "In-House";

export enum ServiceRequestStatus {
  NEW_TASK = "NEW_TASK",
  APPROVED = "APPROVED",
  TO_DO = "TO_DO",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

export interface CommonFilters {
  currentPage?: number;
  perPage?: number;
  sortField?: string | null;
  sortDirection?: "asc" | "desc" | null;
  channels?: string[];
}

export interface ArrivalsFilters extends CommonFilters {
  arrivalDate?: string | null;
  arrivalDateTo?: string | null;
  preCheckinStatus?: string[];
}

export interface InHouseFilters extends CommonFilters {
  nationality?: string[];
}

export interface DeparturesFilters extends CommonFilters {
  departureDate?: string | null;
  preCheckoutStatus?: string[];
  requestsStatus?: ServiceRequestStatus[];
  nationality?: string[];
}

export type SettlementFilters =
  | ArrivalsFilters
  | InHouseFilters
  | (DeparturesFilters & { type: SettlementType; guestId?: string });

export const settlementKeys = {
  all: ["settlement"] as const,
  lists: () => [...settlementKeys.all, "list"] as const,
  list: (filters?: SettlementFilters, hotelId?: string) =>
    [...settlementKeys.lists(), filters, hotelId] as const,
};

export interface SettlementResult {
  loading: boolean;
  reservations: SettlementBooking[];
  total: number;
  totalPages: number;
  error?: Error | null;
  refetch?: () => void;
}

export const useSettlement = (filters: SettlementFilters, hotelId?: string) => {
  const query = useQuery({
    queryKey: settlementKeys.list(filters, hotelId),
    queryFn: () => {
      return hotelService.getSettlement(filters, hotelId);
    },
    enabled: !!hotelId && !!filters,
  });

  return {
    loading: query.isLoading,
    reservations: query.data?.data || [],
    total: query.data?.total || 0,
    totalPages: Math.ceil((query.data?.total || 0) / (filters?.perPage || 10)),
  };
};

export const useArrivals = (
  filters: ArrivalsFilters,
  hotelId?: string,
): SettlementResult => {
  return useSettlement({ type: "Arrivals", ...filters }, hotelId);
};

export const useInHouse = (
  filters: InHouseFilters,
  hotelId?: string,
): SettlementResult => {
  return useSettlement({ type: "In-House", ...filters }, hotelId);
};

export const useGuestVisits = (
  filters: CommonFilters,
  hotelId?: string,
  guestId?: string,
): SettlementResult => {
  return useSettlement({ type: "Departures", ...filters, guestId }, hotelId);
};

export const useDepartures = (
  filters: DeparturesFilters,
  hotelId?: string,
): SettlementResult => {
  if (
    filters.requestsStatus &&
    filters.requestsStatus.includes(ServiceRequestStatus.IN_PROGRESS) &&
    !filters.requestsStatus.includes(ServiceRequestStatus.TO_DO)
  ) {
    filters.requestsStatus.push(
      ServiceRequestStatus.TO_DO,
      ServiceRequestStatus.APPROVED,
      ServiceRequestStatus.NEW_TASK,
    );
  }
  return useSettlement({ type: "Departures", ...filters }, hotelId);
};
